import type { UseFetchOptions } from "#app";

interface Token {
  access: string;
  refresh: string;
}

interface TokenResponse {
  access_token: string;
  refresh_token: string;
}

export const useAuth = () => {
  const user: Ref = useState('auth:user', () => null);
  const token: Ref<Token | null | any> = useState('auth:token', () => null);
  const status = useState('auth:status', () => 'unauthenticated');
  const config = useRuntimeConfig()
  const baseURL = `${(import.meta.server ? config.loyaltyApiURL : config.public.loyaltyApiURL)}api/loyalty/`;

  const ENDPOINTS = {
    login: 'login',
    me: 'profile',
    refresh: 'refresh',
  }

  async function useAuthFetch(endpoint: string, options: UseFetchOptions<any> = {}) {
    if(import.meta.client) {
      return new Promise(async (resolve, reject) => {
        try {
          const headers = { ...(options.headers || {}) }
          if(status.value === 'authenticated') {
            // @ts-ignore
            headers['Authorization'] = `Bearer ${token.value?.access}`;
          }
          
          try {
            const response = await useClientFetch(endpoint, {
              baseURL,
              ...options,
              headers
            })
            resolve(response)
          } catch(error) {
            throw new Error("Unauthorized", { cause: error });
          }
        } catch(error: any) {
          if((error?.cause?.status === 401 || error?.status === 401) && !endpoint.includes(ENDPOINTS.refresh) ) {
            try {
              // Check if refresh token exists
              if(token.value && token.value.refresh !== null) {
                // Get new tokens if refresh token exists
                await getRefreshToken()
                // Login with new token
                await login()
                resolve(await useAuthFetch(error?.cause?.request || error?.request, error?.cause?.options || error?.options))
              } else throw new Error(error?.cause || error);
            } catch (error) {
              if(error?.cause?.status === 401 || error?.status === 401) await logout()
              reject(error);
            }
          } else reject(error);
        }
      })
    }

    return 
  }

  function setCookie(name: string, value: string, expiration: number = 5 * 60 * 1000) {
    const d = new Date();
    d.setTime(d.getTime() + (expiration));
    const expires = "expires="+ new Date(d.getTime()).toUTCString();
    document.cookie = name + "=" + value + ";" + expires + ";path=/";
  }

  function getCookie(name: string) {
    const decodedCookie = decodeURIComponent(document.cookie);
    const ca = decodedCookie.split(';');
    name += '=';
    for(let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) == ' ') {
        c = c.substring(1);
      }
      if (c.indexOf(name) == 0) {
        return c.substring(name.length, c.length);
      }
    }
    return '';
  }

  function removeCookie(name: string) {
    document.cookie = name +'=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
  }

  function getToken() {
    for(const key of ['access', 'refresh']) {
      const _token = getCookie(`token:${key}`);
      if(_token) {
        token.value = {
          ...(token.value || {}),
          [key]: _token || null
        }
      }
    }

    return token.value || null;
  }

  function getRefreshToken() {
    return new Promise(async (resolve, reject) => {
      try {
        let _baseURL = baseURL.split('/api')[0]
        const response = await useAuthFetch(ENDPOINTS.refresh, {
          baseURL: _baseURL,
          method: 'POST',
          body: { refresh_token: token.value?.refresh },
        }) as TokenResponse

        setToken(response)
        resolve(token.value);
      } catch (error) {
        token.value = null;
        reject(error);
      }
    })
  }

  function setToken(data: Token | TokenResponse) {
    token.value = {
      access: 'access' in data ? data.access : data.access_token,
      refresh: 'refresh' in data ? data.refresh : data.refresh_token,
    };

    setCookie('token:access', token.value.access);
    setCookie('token:refresh', token.value.refresh, 60 * 60 * 1000);
  }

  async function login({ email, password }: { email?: string, password?: string} = {}, options?: any) {
    return new Promise(async (resolve, reject) => {
      try {
        status.value = 'loading';
        getToken();
        if(!token.value) {
          try {
            const response = await useClientFetch(ENDPOINTS.login, {
              ...options,
              baseURL,
              method: 'POST',
              body: { email, password },
            }) as TokenResponse
      
            setToken(response)
          } catch (error) {
            await logout()
            reject(error);
          }
        }

        if(token.value) {
          status.value = 'authenticated';
          resolve(await getUser());
        }
      } catch (error) {
        await logout()
        reject(error);
      }
    })
  };

  function getUser() {
    return new Promise(async (resolve, reject) => {
      try {
        user.value = await useAuthFetch(ENDPOINTS.me);
        resolve(user.value);
      } catch (error) {
        console.error('Get user failed:', error);
        reject(error);
      }
    })
  }

  async function logout() {
    const { links, link } = useSetup()
    const { path } = useRouter().currentRoute.value

    user.value = null;
    token.value = null;
    status.value = 'unauthenticated';
    for (let key of ['access', 'refresh']) {
      removeCookie(`token:${key}`);
    }
    
    if ([links.value['LoyaltyDashboard'], links.value['LoyaltyFAQ'], links.value['Points'], links.value['Profile'], links.value['Stays'], links.value['StaysDetail']].includes(path)) {
      // await navigateTo(link('Login'));
      window.location.href = link('Login');
    }
  };

  return {
    user,
    token,
    status,
    
    login,
    logout,

    getUser,
    getToken,
    useAuthFetch
  };
}